import * as yup from 'yup';

// Solana address validation regex (base58, 32-44 characters)
const SOLANA_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;

interface WalletSchemaOptions {
  allowEvm?: boolean;
}

const isSolanaAddress = (address: string) => SOLANA_ADDRESS_REGEX.test(address);
const isEvmAddress = (address: string) => EVM_ADDRESS_REGEX.test(address);

export const createWalletAddressSchema = (options: WalletSchemaOptions = {}) => {
  const { allowEvm = false } = options;
  const errorMessage = allowEvm
    ? 'Invalid wallet address — expected a Polygon (0x…) address.'
    : 'Invalid wallet address — expected a Solana (base58) address.';

  return yup.object().shape({
    walletAddress: yup
      .string()
      .required('Wallet address is required')
      .test('wallet-format', errorMessage, value => {
        if (!value) {
          return false;
        }
        if (allowEvm) {
          // Polygon-only context: reject Solana base58 even though it would
          // pass the more permissive cross-chain check.
          return isEvmAddress(value);
        }
        // Solana-only context (default): reject EVM hex addresses.
        return isSolanaAddress(value);
      }),
  });
};

export const walletAddressSchema = createWalletAddressSchema();

// Helper function for validation
export const validateWalletAddress = async (
  address: string,
  options?: WalletSchemaOptions,
): Promise<boolean> => {
  try {
    await createWalletAddressSchema(options).validate({ walletAddress: address });
    return true;
  } catch (error) {
    return false;
  }
};

// Export individual field schema for reuse
export const walletAddressFieldSchema = walletAddressSchema.fields.walletAddress;

/**
 * Quick syntactic length check used to gate the Continue button before the
 * user submits. Cheap (no Yup roundtrip) so it can run on every keystroke.
 *
 * Solana base58 addresses are 32–44 chars; Polygon hex addresses are exactly
 * 0x + 40 hex chars. Anything shorter than the minimum is definitely invalid.
 */
export const looksValidLength = (address: string, chain: 'polygon' | 'solana'): boolean => {
  const value = (address || '').trim();
  if (!value) return false;
  if (chain === 'solana') return value.length >= 32 && value.length <= 44;
  // Polygon: '0x' + 40 hex
  return value.length === 42 && value.startsWith('0x');
};
